import React from "react";
import { FiMoreVertical } from "react-icons/fi";
import { ClickAwayListener } from "@mui/material";

import {
  TableContainer,
  StyledTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  ActionButton,
  ActionsWrapper,
  ActionsDropdownMenu,
  ActionOption
} from "./styles";

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => React.ReactNode;
}

export interface TableAction<T> {
  label: string;
  icon?: React.ReactNode;
  onClick: (row: T, event?: React.MouseEvent) => void;
  hidden?: (row: T) => boolean;
  disabled?: (row: T) => boolean;
  position?: "menu" | "outside";
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  actions?: TableAction<T>[];
  rowClassName?: (row: T) => string;
}

export function Table<T>({ columns, data, onRowClick, actions, rowClassName }: TableProps<T>) {
  return (
    <TableContainer>
      <StyledTable>
        <Thead>
          <Tr>
            {columns.map((col) => (
              <Th key={col.key}>{col.header}</Th>
            ))}
            {actions && actions.length > 0 && <Th className="actions-header"></Th>}
          </Tr>
        </Thead>
        <Tbody>
          {data.map((row, rowIndex) => (
            <Tr
              key={rowIndex}
              className={rowClassName ? rowClassName(row) : undefined}
              clickable={!!onRowClick}
              onClick={() => onRowClick && onRowClick(row)}
            >
              {columns.map((col) => (
                <Td key={col.key} data-label={col.header}>
                  {col.render ? col.render(row) : (row as any)[col.key]}
                </Td>
              ))}
              {actions && actions.length > 0 && (
                <Td className="actions-cell">
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", justifyContent: "flex-end", width: "100%" }}>
                    {actions
                      .filter(a => a.position === "outside" && !(a.hidden && a.hidden(row)))
                      .map((action, idx) => {
                        const isDisabled = action.disabled && action.disabled(row);
                        return (
                          <ActionButton
                            key={`outside-${idx}`}
                            disabled={isDisabled}
                            style={{ opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? "not-allowed" : "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isDisabled) action.onClick(row, e);
                            }}
                            title={action.label}
                          >
                            {action.icon}
                          </ActionButton>
                        );
                      })}
                    {actions.filter(a => a.position !== "outside" && !(a.hidden && a.hidden(row))).length > 0 && (
                      <ActionsMenu row={row} actions={actions.filter(a => a.position !== "outside" && !(a.hidden && a.hidden(row)))} />
                    )}
                  </div>
                </Td>
              )}
            </Tr>
          ))}
        </Tbody>
      </StyledTable>
    </TableContainer>
  );
}

function ActionsMenu<T>({ row, actions }: { row: T; actions: TableAction<T>[] }) {
  const [open, setOpen] = React.useState(false);

  const toggleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpen((prev) => !prev);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <ActionsWrapper>
        <ActionButton isOpen={open} onClick={toggleOpen}>
          <FiMoreVertical size={20} />
        </ActionButton>
        
        {open && (
          <ActionsDropdownMenu className="table-actions-dropdown" onClick={(e) => e.stopPropagation()}>
            {actions.map((action, idx) => {
              if (action.hidden && action.hidden(row)) return null;
              
              const isDisabled = action.disabled && action.disabled(row);
              
              return (
                <ActionOption
                  key={idx}
                  disabled={isDisabled}
                  onClick={(e) => {
                    if (isDisabled) {
                      e.preventDefault();
                      return;
                    }
                    handleClose();
                    action.onClick(row);
                  }}
                >
                  {action.icon}
                  <span>{action.label}</span>
                </ActionOption>
              );
            })}
          </ActionsDropdownMenu>
        )}
      </ActionsWrapper>
    </ClickAwayListener>
  );
}
